// ESC/POS 영수증 커맨드 빌더 (웹 소유)
// 영수증 포맷은 여기서만 관리한다 — 셸은 printer:printRaw로 바이트를 그대로 출력할 뿐이므로
// 포맷 변경 시 셸 재배포가 필요 없다. (구버전 셸은 printer:print 폴백으로 셸 내장 포맷 사용)

import { Buffer } from 'buffer'
import iconv from 'iconv-lite'
import type { PrinterConfig, ReceiptData } from '@abcpay/shared'

// ESC/POS Commands
const ESC = 0x1b
const GS = 0x1d
const LF = 0x0a

const ALIGN_CENTER = Uint8Array.from([ESC, 0x61, 0x01])
const ALIGN_LEFT = Uint8Array.from([ESC, 0x61, 0x00])

const TEXT_NORMAL = Uint8Array.from([ESC, 0x21, 0x00])
const TEXT_BOLD = Uint8Array.from([ESC, 0x21, 0x08])
const TEXT_DOUBLE_SIZE = Uint8Array.from([ESC, 0x21, 0x30])

const PAPER_CUT = Uint8Array.from([GS, 0x56, 0x42, 0x00])
const INIT_PRINTER = Uint8Array.from([ESC, 0x40])
const NEWLINE = Uint8Array.from([LF])
const FEED_AND_CUT = Uint8Array.from([LF, LF, LF])

// 숫자 포맷팅 (3자리 쉼표)
function formatNumber(num: string | number): string {
  const n = parseFloat(String(num))
  if (Number.isNaN(n)) return String(num)
  const parts = n.toString().split('.')
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return parts.join('.')
}

// EUC-KR 인코딩 (국내 영수증 프린터 표준)
function encodeText(text: string): Uint8Array {
  return Uint8Array.from(iconv.encode(text, 'euc-kr'))
}

function formatDate(date: Date): string {
  const d = new Date(date)
  return d.toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

class CommandBuilder {
  private chunks: Uint8Array[] = []

  push(...items: (Uint8Array | string)[]) {
    for (const item of items) {
      this.chunks.push(typeof item === 'string' ? encodeText(item) : item)
    }
    return this
  }

  line(text: string) {
    return this.push(text, NEWLINE)
  }

  build(): Uint8Array {
    const total = this.chunks.reduce((sum, c) => sum + c.length, 0)
    const out = new Uint8Array(total)
    let offset = 0
    for (const c of this.chunks) {
      out.set(c, offset)
      offset += c.length
    }
    return out
  }
}

// 영수증 ESC/POS 커맨드 생성 (셸 buildReceiptCommands와 동일한 레이아웃)
export function buildReceiptCommands(data: ReceiptData, config: PrinterConfig): Uint8Array {
  const b = new CommandBuilder()
  const charsPerLine = config.charactersPerLine || 32
  const tx = data.transaction

  // Header
  b.push(INIT_PRINTER, ALIGN_CENTER, TEXT_DOUBLE_SIZE)
  b.line(data.storeInfo.name)
  b.push(TEXT_NORMAL)
  if (data.storeInfo.address) b.line(data.storeInfo.address)
  if (data.storeInfo.phone) b.line(`Tel: ${data.storeInfo.phone}`)
  if (data.storeInfo.businessNumber) {
    b.line(`사업자번호: ${data.storeInfo.businessNumber}`)
  }
  b.line('='.repeat(charsPerLine))

  // Receipt number and date
  b.push(ALIGN_LEFT)
  b.line(`영수증번호: ${data.receiptNumber}`)
  b.line(`일시: ${formatDate(data.timestamp)}`)
  b.line('-'.repeat(charsPerLine))

  // Transaction type
  const tradeTypeText = tx.exchangePair
    ? '교환 (EXCHANGE)'
    : tx.tradeType === 'buy' ? '매입 (BUYING)' : '매각 (SELLING)'
  b.push(TEXT_BOLD)
  b.line(`거래유형: ${tradeTypeText}`)
  b.push(TEXT_NORMAL)

  if (tx.exchangePair) {
    // 통화간 교환 (#7): 매입 + 매각 + 원화 거스름돈
    const { buy, sell, changeKRW } = tx.exchangePair
    b.push(TEXT_BOLD).line(`[매입] ${buy.currency}`).push(TEXT_NORMAL)
    b.line(`  환율: ${formatNumber(buy.rate)}`)
    b.line(`  외화금액: ${formatNumber(buy.foreignAmount)} ${buy.currency}`)
    b.line(`  원화금액: ${formatNumber(buy.krwAmount)} KRW`)
    b.line('-'.repeat(charsPerLine))

    b.push(TEXT_BOLD).line(`[매각] ${sell.currency}`).push(TEXT_NORMAL)
    b.line(`  환율: ${formatNumber(sell.rate)}`)
    b.line(`  외화금액: ${formatNumber(sell.foreignAmount)} ${sell.currency}`)
    b.line(`  원화금액: ${formatNumber(sell.krwAmount)} KRW`)
    b.line('-'.repeat(charsPerLine))

    b.push(TEXT_BOLD).line(`거스름돈: ${formatNumber(changeKRW)} KRW`).push(TEXT_NORMAL)
  } else if (tx.lines && tx.lines.length > 0) {
    // 복수 거래 항목 (#5, #9): 통화/환율별 리스트 + 하단 총액
    for (const line of tx.lines) {
      b.push(TEXT_BOLD).line(`[${line.currency}]`).push(TEXT_NORMAL)
      b.line(`  환율: ${formatNumber(line.rate)}`)
      b.line(`  외화금액: ${formatNumber(line.foreignAmount)} ${line.currency}`)
      b.line(`  원화금액: ${formatNumber(line.krwAmount)} KRW`)
      b.line('-'.repeat(charsPerLine))
    }
    const totalLabel = tx.tradeType === 'buy' ? '지급 총액' : '수취 총액'
    b.push(TEXT_BOLD)
    b.line(`${totalLabel}: ${formatNumber(tx.totalKRW ?? tx.krwAmount)} KRW`)
    b.push(TEXT_NORMAL)
  } else {
    // 단일 통화
    b.line(`통화: ${tx.selectedCurrency}`)
    b.line(`환율: ${formatNumber(tx.rate)}`)
    b.line(`외화금액: ${formatNumber(tx.foreignAmount)} ${tx.selectedCurrency}`)
    b.line(`원화금액: ${formatNumber(tx.krwAmount)} KRW`)
    if (tx.tradeType === 'sell' && tx.receivedKRW) {
      b.line(`받은 원화: ${formatNumber(tx.receivedKRW)} KRW`)
      if (tx.change) {
        b.line(`거스름돈: ${formatNumber(tx.change)} KRW`)
      }
    }
  }

  // Footer
  b.line('='.repeat(charsPerLine))
  b.push(ALIGN_CENTER)
  b.line('감사합니다')
  b.push('Thank you', FEED_AND_CUT, PAPER_CUT)

  return b.build()
}

// Uint8Array → base64 (브라우저 안전, 청크 처리)
export function toBase64(bytes: Uint8Array): string {
  let binary = ''
  const CHUNK = 0x8000
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK))
  }
  return btoa(binary)
}

// iconv-lite가 내부에서 Buffer를 사용하므로 참조 유지 (트리셰이킹 방지)
export { Buffer }
